import React, { useEffect, useState } from "react";
import { Box, Divider, TableCell, Tooltip, TableRow } from "@mui/material";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/InterestExpensePdf";
import CalculationSummary from "../utils/CalculationSummary";
import {
  formatAmount,
  formatHistoryAmount,
  isAutoload
} from "../../../utils/hooks/dataHandler";
import { JustDropDown } from "../../utils/Form/Dropdown";
import ScheduleHeader from "../../App/ScheduleHeader";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable from "../../utils/CommonTable";
import {
  TextFieldForName,
  TextFieldForViewOnly
} from "../../utils/TextField/TextField";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SectionBox from "../utils/SectionBox";
import ScheduleContainer from "../utils/ScheduleContainer";

const incomeProducingOptionSet = [
  "Income Producing Asset",
  "Non-income producing assets"
];

const InterestExpenseSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true
}) => {
  const [schName, setSchName] = useState("Interest Expense");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [interestExpenseRows, setInterestExpenseRows] = useState([]);
  const [assetsRows, setAssetsRows] = useState([]);
  const [totalInterestExpenses, setTotalInterestExpenses] = useState(0);
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalNonIncomeAssets, setTotalNonIncomeAssets] = useState(0);
  const [disAllowableInterest, setDisallowableInterest] = useState(0);

  const handleSave = () => {
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      interestExpenseRows: interestExpenseRows,
      assetsRows: assetsRows,
      totalAdjustment: disAllowableInterest,
      doc: JSON.stringify({
        interestExpenseRows,
        assetsRows,
        totalInterestExpenses,
        totalAssets,
        totalNonIncomeAssets,
        disAllowableInterest,
        notes,
        reviewNotes
      })
    });
  };

  const recalculateDisallowableInterest = (assetsRows, interestRows) => {
    const totalAssets = CommonCalculator.arraySum(assetsRows);
    const totalNonIncomeAssets = CommonCalculator.arraySum(
      assetsRows.filter((row) => !row.isIncomeProducing)
    );
    const totalInterestExpenses = CommonCalculator.arraySum(interestRows);

    const disAllowableInterest =
      totalAssets && totalInterestExpenses
        ? (totalNonIncomeAssets / totalAssets) * totalInterestExpenses
        : 0;

    return {
      totalAssets,
      totalNonIncomeAssets,
      totalInterestExpenses,
      disAllowableInterest
    };
  };

  const handleInterestExpenseChange = (value, { index, field }) => {
    const updatedRows = [...interestExpenseRows];

    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
    } else {
      updatedRows[index][field] = value;
    }

    setInterestExpenseRows(updatedRows);

    const {
      totalAssets,
      totalNonIncomeAssets,
      totalInterestExpenses,
      disAllowableInterest
    } = recalculateDisallowableInterest(assetsRows, updatedRows);

    setTotalAssets(totalAssets);
    setTotalNonIncomeAssets(totalNonIncomeAssets);
    setTotalInterestExpenses(totalInterestExpenses);
    setDisallowableInterest(disAllowableInterest);
  };

  const handleAssetChange = (value, { index, field }) => {
    const updatedRows = [...assetsRows];

    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
    } else {
      updatedRows[index][field] = value;
    }

    setAssetsRows(updatedRows);

    const {
      totalAssets,
      totalNonIncomeAssets,
      totalInterestExpenses,
      disAllowableInterest
    } = recalculateDisallowableInterest(updatedRows, interestExpenseRows);

    setTotalAssets(totalAssets);
    setTotalNonIncomeAssets(totalNonIncomeAssets);
    setTotalInterestExpenses(totalInterestExpenses);
    setDisallowableInterest(disAllowableInterest);
  };

  const addRow = (setRows) => {
    setRows((prevRows) => [
      ...prevRows,
      {
        isIncomeProducing: false,
        description: "",
        amount: "",
        reference: ""
      }
    ]);
  };

  const removeRow = (setRows, index) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setAssetsRows(
        scheduleDetails.assetsRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );
      setInterestExpenseRows(
        scheduleDetails.interestExpenseRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );

      //calculation
      let c_totalAssets = CommonCalculator.arraySum(scheduleDetails.assetsRows);
      let c_totalNonIncomeAssets = CommonCalculator.arraySum(
        scheduleDetails.assetsRows.filter((row) => !row.isIncomeProducing)
      );
      let c_totalInterestExpense = CommonCalculator.arraySum(
        scheduleDetails.interestExpenseRows
      );
      setTotalInterestExpenses(c_totalInterestExpense);
      setTotalNonIncomeAssets(c_totalNonIncomeAssets);
      setTotalAssets(c_totalAssets);
      setDisallowableInterest(
        (c_totalNonIncomeAssets / c_totalAssets) * c_totalInterestExpense
      );
    }
  }, []);

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          deleteSchedule,
          canEditSchedule
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          interestExpenseRows={interestExpenseRows}
          assetsRows={assetsRows}
          totalInterestExpenses={totalInterestExpenses}
          totalAssets={totalAssets}
          totalNonIncomeAssets={totalNonIncomeAssets}
          disAllowableInterest={disAllowableInterest}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schedule.name}
        taxAdjustment={"Added Back"}
        allowedToEditSchName
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            {/* Interest expense */}
            <SectionBox
              title='Interest Expenses'
              onAdd={() => addRow(setInterestExpenseRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={interestExpenseRows}
                columns={[
                  {
                    key: "description",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row.description}
                        onChange={handleInterestExpenseChange}
                        reference={{
                          index: index,
                          field: "description"
                        }}
                        disabled={isAutoload(row.status)}
                        canEdit={canEditSchedule}
                      />
                    )
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <Tooltip
                        title={
                          row?.historyAmount &&
                          row.historyAmount.length > 0 &&
                          formatHistoryAmount(row.historyAmount)
                        }
                        placement='top'
                      >
                        <Box display='inline-block' sx={{ width: "100%" }}>
                          <CalculatorTextField
                            value={row?.historyAmount || []}
                            onChange={handleInterestExpenseChange}
                            reference={{
                              index: index,
                              field: "amount"
                            }}
                            fullWidth={true}
                            disabled={isAutoload(row.status)}
                            canEdit={canEditSchedule}
                          />
                        </Box>
                      </Tooltip>
                    )
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={handleInterestExpenseChange}
                        reference={{
                          index: index,
                          field: "reference"
                        }}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    )
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly value={row.source?.sch_no || ""} />
                    )
                  },
                  canEditSchedule
                    ? {
                        key: "action",
                        label: "Action",
                        headerAlign: "center",
                        render: (row, index) => (
                          <IconButtonWithTooltip
                            icon={DeleteOutlineIcon}
                            tooltipTitle='Delete Row'
                            onClick={() =>
                              removeRow(setInterestExpenseRows, index)
                            }
                            color='error'
                            size='small'
                            disabled={isAutoload(row.status)}
                          />
                        )
                      }
                    : []
                ]}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  Total Interest Expenses:
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(totalInterestExpenses)}
                </TableCell>
                <TableCell colSpan={3} />
              </CommonTable>
            </SectionBox>

            <Divider sx={{ marginY: 4 }} />
            {/* Asset Rows */}
            <SectionBox
              title='Assets at cost'
              onAdd={() => addRow(setAssetsRows)}
            >
              <CommonTable
                rows={assetsRows}
                columns={[
                  {
                    key: "description",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row.description}
                        onChange={handleAssetChange}
                        reference={{
                          index: index,
                          field: "description"
                        }}
                        canEdit={canEditSchedule}
                      />
                    )
                  },
                  {
                    key: "isIncomeProducing",
                    label: "Asset Type",
                    headerAlign: "center",
                    colAlign: "center",
                    render: (row, index) => (
                      <JustDropDown
                        label=''
                        maxWidth={300}
                        value={
                          row.isIncomeProducing
                            ? incomeProducingOptionSet[0]
                            : incomeProducingOptionSet[1]
                        }
                        options={incomeProducingOptionSet}
                        onChange={(e) =>
                          handleAssetChange(
                            e.target.value == incomeProducingOptionSet[0],
                            { index: index, field: "isIncomeProducing" }
                          )
                        }
                        size='small'
                        canEdit={canEditSchedule}
                      />
                    )
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <Tooltip
                        title={
                          row?.historyAmount &&
                          row.historyAmount.length > 0 &&
                          formatHistoryAmount(row.historyAmount)
                        }
                        placement='top'
                      >
                        <Box display='inline-block' sx={{ width: "100%" }}>
                          <CalculatorTextField
                            value={row?.historyAmount || []}
                            onChange={handleAssetChange}
                            reference={{
                              index: index,
                              field: "amount"
                            }}
                            fullWidth={true}
                            canEdit={canEditSchedule}
                          />
                        </Box>
                      </Tooltip>
                    )
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference || ""}
                        onChange={handleAssetChange}
                        reference={{
                          index: index,
                          field: "reference"
                        }}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    )
                  },
                  canEditSchedule
                    ? {
                        key: "action",
                        label: "Action",
                        headerAlign: "center",
                        render: (row, index) => (
                          <IconButtonWithTooltip
                            icon={DeleteOutlineIcon}
                            tooltipTitle='Delete Row'
                            onClick={() => removeRow(setAssetsRows, index)}
                            color='error'
                            size='small'
                          />
                        )
                      }
                    : []
                ]}
              >
                <TableCell />
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  Non-income producting assets:
                  <br />
                  Total Assets:
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(totalNonIncomeAssets)}
                  <br />
                  {formatAmount(totalAssets)}
                </TableCell>
                <TableCell colSpan={2} />
              </CommonTable>
            </SectionBox>
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />
      {/* Disallowable interest expense */}
      <CalculationSummary
        steps={[
          {
            section: "Total Non-income Producing Assets",
            amount: formatAmount(totalNonIncomeAssets),
            tooltip:
              "The total value of assets that do not generate income. This is used to calculate the proportion of interest that may be disallowed."
          },
          {
            section: "Total Assets",
            amount: formatAmount(totalAssets),
            tooltip:
              "The total value of all assets. This is used as the denominator in calculating the disallowable interest ratio."
          },
          {
            section: "Non-income Producing Assets Ratio",
            amount:
              totalAssets && totalAssets !== 0
                ? `${((totalNonIncomeAssets / totalAssets) * 100).toFixed(2)}%`
                : "0.00%",
            tooltip:
              totalAssets && totalAssets !== 0
                ? "The percentage of total assets that are non-income producing. This ratio determines what portion of interest expenses may be disallowed."
                : "Ratio cannot be calculated as Total Assets is zero or invalid."
          },
          {
            section: "Total Interest Expense",
            amount: formatAmount(totalInterestExpenses),
            tooltip:
              "The total interest expenses incurred. This amount is multiplied by the non-income producing assets ratio to determine the disallowable portion."
          }
        ]}
        total={{
          title: "Disallowable Interest Expense",
          amount: formatAmount(disAllowableInterest),
          tooltip:
            "Formula: (Total Non-income Producing Assets / Total Assets) × Total Interest Expense. This represents the portion of interest expenses that may be disallowed for tax purposes."
        }}
      />
      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default InterestExpenseSchedule;
